import '@material/web/icon/icon.js'

import gql from 'graphql-tag'
import { css, html, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import { i18next, localize } from '@operato/i18n'
import { client } from '@operato/graphql'
import { SingleColumnFormStyles } from '@operato/form'
import { CommonHeaderStyles } from '@operato/styles'

import moment from '@operato/moment-timezone-es'
import { CustomAlert } from '@operato/shell'

@customElement('data-archive-request-popup')
class DataArchiveRequestPopup extends localize(i18next)(LitElement) {
  static styles = [
    CommonHeaderStyles,
    SingleColumnFormStyles,
    css`
      :host {
        padding: 10px;
        display: flex;
        flex-direction: column;
        overflow-x: overlay;
        background-color: var(--md-sys-color-background);
      }

      form {
        overflow: auto;
      }
    `
  ]

  @state() dataSetTypes: any

  render() {
    return html`
      <form id="input-form" name="generation" class="single-column-form" @submit=${e => this.onSubmit(e)}>
        <fieldset>
          <label>${i18next.t('label.start-date')}</label>
          <input type="month" name="startDate" required />
          <label>${i18next.t('label.end-date')}</label>
          <input type="month" name="endDate" .value=${moment().format('YYYY-MM')} />
          <label>${i18next.t('label.data-set-type')}</label>
          <select name="type" required>
            ${(this.dataSetTypes || []).map(t => html`<option value=${t && t.value}>${t && t.display}</option>`)}
          </select>
        </fieldset>
      </form>

      <div class="footer">
        <div filler></div>
        <button @click=${this.requestArchive} done>
          <md-icon>archive</md-icon>${String(i18next.t('button.submit'))}
        </button>
      </div>
    `
  }

  async firstUpdated() {}

  async onSubmit(e) {
    e.preventDefault()
    this.requestArchive()
  }

  serializeFormData() {
    const obj = {}

    Array.from(this.shadowRoot!.querySelectorAll('form#input-form input, select')).forEach((field: Element) => {
      const input = field as HTMLInputElement
      if (!input.hasAttribute('hidden') && input.value) {
        obj[input.name] = input.type === 'checkbox' ? input.checked : input.value
      }
    })

    if (obj['startDate']) {
      obj['startDate'] += '-01'
    }

    if (obj['endDate']) {
      const endDate = moment(obj['endDate']).endOf('month')
      const today = moment()
      const format = 'YYYY-MM-DD'

      obj['endDate'] = endDate > today ? today.format(format) : endDate.format(format)
    }

    return obj
  }

  /** request download url. */
  async requestArchive() {
    // #1 request download url
    const dataArchive = {
      requestParams: this.serializeFormData(),
      status: 'requested'
    }

    const response = await client.mutate({
      mutation: gql`
        mutation createDataArchive($dataArchive: NewDataArchive!) {
          createDataArchive(dataArchive: $dataArchive) {
            id
            status
          }
        }
      `,
      variables: { dataArchive }
    })

    if (!response.errors) {
      this.dispatchEvent(new CustomEvent('requested', {}))

      const {
        createDataArchive: { id }
      } = response.data
      dataArchive['id'] = id

      this._generateArchiveAndDownloadUrl(dataArchive)

      await CustomAlert({
        type: 'info',
        title: i18next.t('title.ready'),
        text: i18next.t('text.data-archive waits'),
        confirmButton: { text: i18next.t('button.confirm') }
      })
    } else {
      console.error(response.errors)
      this.showToast(i18next.t('text.failed'))
    }
  }

  async _generateArchiveAndDownloadUrl(dataArchive) {
    const response = await client.mutate({
      mutation: gql`
        mutation generatePresignedUrl($patch: DataArchivePatch!) {
          generatePresignedUrl(patch: $patch) {
            id
            downloadUrl
            status
          }
        }
      `,
      variables: { patch: dataArchive }
    })

    if (!response.errors) {
      this.showToast(i18next.t('title.data-archive downloads ready'))
      this.dispatchEvent(new CustomEvent('created', {}))
    } else {
      console.error(response.errors)
      this.showToast(i18next.t('text.failed'))
    }
  }

  showToast(message) {
    document.dispatchEvent(new CustomEvent('notify', { detail: { message } }))
  }

  constructor() {
    super()

    this.dataSetTypes = [
      {},
      {
        display: i18next.t('text.manually collected'),
        value: 'manual'
      },
      {
        display: i18next.t('text.automatically collected'),
        value: 'automatic'
      }
    ]
  }
}
